import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { useToast } from '@/components/ui/use-toast';

interface LoginFormProps {
  redirectUrl?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ redirectUrl: _redirectUrl = '/' }) => {
  const { login, error: authError, isLoading } = useAuth();
  useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<{ email?: string; password?: string }>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [supabaseConfigChecked, setSupabaseConfigChecked] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    setSupabaseConfigChecked(true);
  }, []);

  // Update form error when auth error changes
  useEffect(() => {
    if (authError) {
      setFormError(authError);
    }
  }, [authError]);

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};
    let isValid = true;

    // Email validation
    if (!email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }

    // Password validation
    if (!password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await login(email, password);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during login';
      console.error('Login form error:', errorMessage);
      
      // Provide better diagnosis for common errors
      if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
        setFormError('Unable to connect. Please check your internet connection and try again.');
      } else if (errorMessage.includes('Invalid')) {
        setFormError('Invalid email or password. Please try again.');
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="w-full">
      {!supabaseConfigChecked && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-xl flex items-start shadow-sm">
          <svg className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm">Checking authentication configuration...</span>
        </div>
      )}
      
      {import.meta.env.DEV && (
        <div className="mb-6 p-4 bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-xs shadow-sm">
          <p className="font-semibold mb-2 text-slate-900">Developer Quick Credentials:</p>
          <div className="space-y-1 text-slate-600 mb-3">
            <div className="flex items-center">
              <span className="w-24 inline-block font-medium">Supabase URL:</span> 
              <span className={import.meta.env.VITE_SUPABASE_URL ? 'text-green-600' : 'text-red-500'}>
                {import.meta.env.VITE_SUPABASE_URL ? '✓ Configured' : '✗ Missing'}
              </span>
            </div>
            <div className="flex items-center">
              <span className="w-24 inline-block font-medium">Anon Key:</span> 
              <span className={import.meta.env.VITE_SUPABASE_ANON_KEY ? 'text-green-600' : 'text-red-500'}>
                {import.meta.env.VITE_SUPABASE_ANON_KEY ? '✓ Configured' : '✗ Missing'}
              </span>
            </div>
          </div>
          <button 
            type="button"
            onClick={() => {
              setEmail('patient@kaimed.health');
              setPassword('Patient123!');
            }}
            className="w-full py-2 px-3 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-lg transition-colors font-medium border border-slate-300"
          >
             Use Test Patient Account
          </button>
        </div>
      )}
      
      {formError && (
        <div className="mb-6 p-4 bg-red-50/80 border border-red-200 text-red-700 rounded-xl shadow-sm text-sm">
          <div className="flex mb-2">
            <svg className="h-5 w-5 mr-2 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium align-middle">{formError}</span>
          </div>
          {formError.includes('connect') && (
            <p className="mt-2 pl-7 text-xs text-red-600/90">
              If the issue persists, please try again in a few moments or contact support@kaimed.health.
            </p>
          )}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
            Email address
          </label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#3D7A74] transition-colors">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </div>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                validationErrors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
              }`}
              placeholder="patient@example.com"
            />
          </div>
          {validationErrors.email && (
            <p className="mt-1.5 text-xs text-red-500 flex items-center">
              <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
              {validationErrors.email}
            </p>
          )}
        </div>
        
        <div>
          <div className="flex justify-between items-center mb-1.5">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 transition-colors group-focus-within:text-[#3D7A74]">
              Password
            </label>
            <Link to="/forgot-password" className="text-xs font-semibold text-[#3D7A74] hover:text-[#2d5e59] transition-colors">
              Forgot password?
            </Link>
          </div>
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#3D7A74] transition-colors">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`w-full pl-10 pr-12 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                validationErrors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              )}
            </button>
          </div>
          {validationErrors.password && (
            <p className="mt-1.5 text-xs text-red-500 flex items-center">
              <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
              {validationErrors.password}
            </p>
          )}
        </div>
        
        <div className="pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-md shadow-[#3D7A74]/20 text-sm font-semibold text-white bg-[#3D7A74] hover:bg-[#32645f] hover:shadow-lg hover:shadow-[#3D7A74]/30 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3D7A74] disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-md transition-all duration-200"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Authenticating...
              </>
            ) : (
              'Sign in to your account'
            )}
          </button>
        </div>
      </form>
      
      <div className="mt-8 relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 bg-white text-xs text-gray-500 uppercase tracking-wider font-medium">New to KaiMed?</span>
        </div>
      </div>
      
      <div className="mt-6 text-center">
        <Link 
          to="/register" 
          className="inline-flex w-full justify-center py-3 px-4 border border-gray-300 rounded-xl shadow-sm bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3D7A74] transition-all duration-200"
        >
          Create an account
        </Link>
      </div>
    </div>
  );
};

export default LoginForm; 